import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/*
 * STOWENA S103 (2026-09-22) — hover intent for the desktop mega menu.
 *
 * Stock Horizon opens a panel on the very first `pointerenter` and closes it on the
 * very first `pointerleave` of the <li>. Measured on MAIN (v13.8.1) that meant:
 *   - a diagonal move into the panel outran the 0.36 s wipe, the pointer landed on
 *     the page underneath, `pointerleave` fired and the panel closed (20/20 runs);
 *   - a fast sweep across the bar opened and closed every panel in turn (20/20);
 *   - leaving for 200 ms and coming back closed and re-opened the panel (20/20).
 *
 * This file keeps every stock behaviour (overflow "More" menu, focus/blur, the
 * --submenu-height / --full-open-header-height variables) and adds:
 *   - OPEN_DELAY: a pointer must rest on a trigger for ~80 ms before its panel opens,
 *     so a sweep opens nothing; focus (keyboard) still opens immediately;
 *   - CLOSE_DELAY: after the pointer leaves both the trigger and the panel, the panel
 *     waits ~300 ms and closes only if the pointer is still outside the "open area"
 *     (the trigger's column down to the header edge, plus the panel's full final
 *     rectangle, measured from --full-open-header-height, not from the wipe);
 *   - one panel at a time: opening one item closes the previous one in the same call;
 *   - click / tap toggles a panel (the trigger link no longer navigates: every panel
 *     carries its own "Shop all" link); a click on a panel that hover just opened
 *     keeps it open, the next click closes it;
 *   - Escape closes the open panel and returns focus to its trigger;
 *   - a pointerdown outside the menu closes a panel that was opened by click or tap;
 *   - aria-expanded stays truthful through every path.
 */
const OPEN_DELAY = 80;
const CLOSE_DELAY = 300;
const AIM_DY = 8; // px of vertical travel inside OPEN_DELAY that marks a pointer as "still moving"
const MAX_AIM_CHECKS = 8; // a sibling trigger opens after at most 8 x OPEN_DELAY even if the pointer keeps drifting

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {number | null} */
  #openTimer = null;
  /** @type {number | null} */
  #closeTimer = null;
  /** @type {HTMLElement | null} */
  #pendingItem = null;
  /** @type {'hover' | 'click' | 'focus' | null} */
  #openedBy = null;
  #pointer = { x: -1, y: -1 };
  /** @type {{ t: number, x: number, y: number }[]} the last few pointer positions, for menu aim */
  #trail = [];
  /** focus events before this timestamp are ignored (set when Escape hands focus back to the trigger) */
  #ignoreFocusUntil = 0;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('click', this.#clickListener);
    document.addEventListener('keydown', this.#keydownListener);
    document.addEventListener('pointermove', this.#pointerMoveListener, { passive: true });
    document.addEventListener('pointerdown', this.#pointerDownListener, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('click', this.#clickListener);
    document.removeEventListener('keydown', this.#keydownListener);
    document.removeEventListener('pointermove', this.#pointerMoveListener);
    document.removeEventListener('pointerdown', this.#pointerDownListener);
    this.#clearTimers();
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#scheduleClose();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Entry point for `on:pointerenter` and `on:focus` on each menu item.
   * Pointer: open after OPEN_DELAY (hover intent). Focus: open immediately.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const isPointer = event.type === 'pointerenter';
    const pointerType = /** @type {PointerEvent} */ (event).pointerType;

    // Touch and pen open by tap (click), never by the synthetic hover that precedes a tap.
    if (isPointer && pointerType && pointerType !== 'mouse') return;

    if (item === this.#state.activeItem) {
      // Re-entering the open item (or its panel) cancels a pending close.
      this.#clearCloseTimer();
      return;
    }

    if (!isPointer) {
      // Focus returned by our own Escape handler must not re-open the panel just closed.
      if (performance.now() < this.#ignoreFocusUntil) return;
      this.#clearTimers();
      this.#open(item, event.target, 'focus');
      return;
    }

    this.#clearOpenTimer();
    this.#pendingItem = item;
    const target = event.target;
    let checks = 0;
    const tryOpen = () => {
      this.#openTimer = null;
      if (this.#pendingItem !== item) return;
      // Menu aim: while another panel is open, a sibling trigger only takes over once the
      // pointer has settled on it. A pointer still travelling vertically (a diagonal from
      // the open trigger into its panel that grazes this trigger's row) keeps waiting.
      if (this.#state.activeItem && this.#state.activeItem !== item && this.#pointerTravellingVertically()) {
        if (++checks < MAX_AIM_CHECKS) {
          this.#openTimer = window.setTimeout(tryOpen, OPEN_DELAY);
          return;
        }
      }
      this.#pendingItem = null;
      this.#open(item, target, 'hover');
    };
    this.#openTimer = window.setTimeout(tryOpen, OPEN_DELAY);
  };

  /**
   * True when the pointer moved more than AIM_DY px vertically within the last OPEN_DELAY ms.
   */
  #pointerTravellingVertically() {
    const now = performance.now();
    const recent = this.#trail.filter((p) => now - p.t <= OPEN_DELAY + 20);
    if (recent.length < 2) return false;
    const dy = recent[recent.length - 1].y - recent[0].y;
    return Math.abs(dy) > AIM_DY;
  }

  /**
   * Open the panel of `item` now, closing whichever panel was open.
   * This is the stock Horizon `activate` body, unchanged apart from the call site.
   * @param {HTMLElement} item
   * @param {Element} target
   * @param {'hover' | 'click' | 'focus'} openedBy
   */
  #open(item, target, openedBy) {
    if (!this.headerComponent) return;

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#clearCloseTimer();

    if (item == this.#state.activeItem) {
      this.#openedBy = openedBy;
      return;
    }

    const isDefaultSlot = target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.#openedBy = openedBy;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Entry point for `on:pointerleave` and `on:blur` on each menu item.
   * Pointer: cancel a pending open, then close after CLOSE_DELAY unless the pointer
   * comes back. Blur: close immediately unless focus stays inside the item or its panel.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (event.type === 'pointerleave') {
      if (this.#pendingItem && this.#pendingItem === item) {
        this.#clearOpenTimer();
      }
      if (this.#state.activeItem && this.#state.activeItem === item) {
        this.#scheduleClose();
      }
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const related = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    const isMovingWithinMenu = related && menu?.contains(document.activeElement);
    const isMovingToSubmenu = related && event.type === 'blur' && menu?.contains(related);
    const isMovingWithinItem = related && event.target.contains(related);
    const isMovingToOverflowMenu = related && related.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isMovingWithinItem) return;

    // Focus left the item and its panel entirely (Tab out, or a click elsewhere).
    if (this.#state.activeItem && (this.#state.activeItem === item || item === null)) {
      this.#deactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.#clearTimers();
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.#openedBy = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Close after CLOSE_DELAY unless the pointer is back inside the open area by then.
   */
  #scheduleClose() {
    this.#clearCloseTimer();
    if (!this.#state.activeItem) return;
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = null;
      if (!this.#state.activeItem) return;
      if (this.#isPointerInsideOpenArea()) {
        // The pointer came back (or landed on the panel before the wipe reached it): stay open.
        return;
      }
      this.#deactivate();
    }, CLOSE_DELAY);
  }

  /**
   * The area a pointer may occupy without closing the panel: the active trigger's own
   * column from its top down to the header's bottom edge, plus the panel's FINAL
   * rectangle (header bottom to header top + --full-open-header-height, full width).
   * The final rectangle is used on purpose so a pointer that outruns the wipe still counts.
   */
  #isPointerInsideOpenArea() {
    const item = this.#state.activeItem;
    const header = this.headerComponent;
    if (!item || !header) return false;
    const { x, y } = this.#pointer;
    if (x < 0 || y < 0) return false;

    const li = item.closest('li') ?? item;
    const liRect = li.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const fullOpen = parseFloat(header.style.getPropertyValue('--full-open-header-height')) || 0;
    const panelBottom = headerRect.top + fullOpen;
    const gutter = 4;

    const inTrigger =
      x >= liRect.left - gutter && x <= liRect.right + gutter && y >= liRect.top - gutter && y <= headerRect.bottom + gutter;
    const inPanel =
      fullOpen > 0 && x >= headerRect.left && x <= headerRect.right && y >= headerRect.bottom - gutter && y <= panelBottom + gutter;

    return inTrigger || inPanel;
  }

  /** @param {PointerEvent} event */
  #pointerMoveListener = (event) => {
    this.#pointer = { x: event.clientX, y: event.clientY };
    const now = performance.now();
    this.#trail.push({ t: now, x: event.clientX, y: event.clientY });
    if (this.#trail.length > 24) this.#trail.splice(0, this.#trail.length - 24);
    if (!this.#state.activeItem || this.#openedBy === 'click' || this.#openedBy === 'focus') return;
    if (this.#isPointerInsideOpenArea()) {
      this.#clearCloseTimer();
    } else if (this.#closeTimer === null) {
      // The pointer is outside and no leave event scheduled a close (e.g. it jumped): start the clock.
      this.#scheduleClose();
    }
  };

  /**
   * Click or tap on a trigger toggles its panel instead of navigating.
   * @param {MouseEvent} event
   */
  #clickListener = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('.menu-list__link[aria-haspopup]');
    if (!link || !this.contains(link)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; // let modifier clicks open the URL

    event.preventDefault();
    this.#clearTimers();

    if (link === this.#state.activeItem) {
      if (this.#openedBy === 'hover') {
        // Hover already opened it; the click confirms rather than closes.
        this.#openedBy = 'click';
        return;
      }
      this.#deactivate();
      return;
    }

    const li = link.closest('li');
    this.#open(link, li ?? link, 'click');
  };

  /** @param {KeyboardEvent} event */
  #keydownListener = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    const trigger = this.#state.activeItem;
    this.#deactivate();
    if (this.contains(document.activeElement) || document.activeElement === document.body) {
      this.#ignoreFocusUntil = performance.now() + 250;
      trigger.focus({ preventScroll: true });
    }
  };

  /** @param {PointerEvent} event */
  #pointerDownListener = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && (this.contains(event.target) || this.overflowMenu?.contains(event.target))) return;
    this.#deactivate();
  };

  #clearOpenTimer() {
    if (this.#openTimer !== null) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }
    this.#pendingItem = null;
  }

  #clearCloseTimer() {
    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  #clearTimers() {
    this.#clearOpenTimer();
    this.#clearCloseTimer();
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
